import type { SessionWearable, WearableDay, WearableWorkout } from "@training/shared";
import { format } from "./session.ts";

/**
 * The wearable's figures as the screens say them. Nothing here names Whoop:
 * the day the source changes, the labels stay.
 */

/** The workout that was the session: the longest of those recorded while it ran. */
export function mainWorkout(workouts: WearableWorkout[]): WearableWorkout | null {
  const length = (workout: WearableWorkout) => (workout.end ? Date.parse(workout.end) - Date.parse(workout.start) : 0);
  return workouts.reduce<WearableWorkout | null>(
    (best, workout) => (!best || length(workout) > length(best) ? workout : best),
    null,
  );
}

/** 506 → "8 h 26", 45 → "45 min". */
export function formatSleep(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  if (hours === 0) return `${rest} min`;
  return `${hours} h ${String(rest).padStart(2, "0")}`;
}

/** Whoop's own thresholds: green from 67, red up to 33. */
export function recoveryTone(pct: number): "high" | "mid" | "low" {
  if (pct >= 67) return "high";
  return pct <= 33 ? "low" : "mid";
}

export const kcal = (kilojoule: number) => Math.round(kilojoule / 4.184);

/** "Récup 61 % · sommeil 8 h 26 · strain 10,3": a session's line in the journal, the session's strain first. */
export function wearableLine(wearable: SessionWearable | undefined): string | null {
  if (!wearable) return null;
  const parts: string[] = [];
  const day = wearable.day;
  if (day?.recoveryPct != null) parts.push(`récup ${Math.round(day.recoveryPct)} %`);
  if (day?.sleepMinutes != null) parts.push(`sommeil ${formatSleep(day.sleepMinutes)}`);
  const strain = mainWorkout(wearable.workouts)?.strain ?? null;
  if (strain !== null) parts.push(`strain ${format(strain)}`);
  if (parts.length === 0) return null;
  const line = parts.join(" · ");
  return line.charAt(0).toUpperCase() + line.slice(1);
}

export type Metric = "recoveryPct" | "hrvMs" | "sleepMinutes" | "strain";

/** How far back the home screen reads: the sparkline's days, and the average's. */
export const SPARK_DAYS = 14;
export const BASELINE_DAYS = 30;

const shiftDate = (iso: string, days: number) => {
  const date = new Date(`${iso}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** The range the home screen asks for: the average's month, today included. */
export function trendRange(today: string): { from: string; to: string } {
  return { from: shiftDate(today, -BASELINE_DAYS), to: today };
}

export interface Trend {
  metric: Metric;
  label: string;
  value: string;
  /** Today against the last 30 days' average, in the metric's unit, rounded as shown. */
  delta: number | null;
  deltaLabel: string | null;
  /** Up is better for all but the strain, which is neither. */
  better: boolean | null;
  tone?: "high" | "mid" | "low";
  /** The last 14 days, today last; null where nothing was scored. */
  spark: (number | null)[];
}

const LABEL: Record<Metric, string> = { recoveryPct: "Récup", hrvMs: "HRV", sleepMinutes: "Sommeil", strain: "Strain" };

function show(metric: Metric, value: number): string {
  if (metric === "recoveryPct") return `${Math.round(value)} %`;
  if (metric === "hrvMs") return `${Math.round(value)} ms`;
  if (metric === "sleepMinutes") return formatSleep(value);
  return format(value);
}

function showDelta(metric: Metric, delta: number): string {
  const size = Math.abs(delta);
  if (metric === "sleepMinutes") return size >= 60 ? formatSleep(size) : `${Math.round(size)} min`;
  if (metric === "strain") return format(size);
  return `${Math.round(size)}${metric === "hrvMs" ? " ms" : ""}`;
}

/**
 * Today's figures for the home screen, each against its own last month: a
 * value means more next to what is usual for this body. Empty when the
 * wearable has nothing for today.
 */
export function dayTrends(days: WearableDay[], today: string): Trend[] {
  const byDate = new Map(days.map((day) => [day.date, day]));
  const current = byDate.get(today);
  if (!current) return [];

  const trends: Trend[] = [];
  for (const metric of ["recoveryPct", "hrvMs", "sleepMinutes", "strain"] as const) {
    const value = current[metric];
    if (value == null) continue;
    const before: number[] = [];
    for (let back = 1; back <= BASELINE_DAYS; back += 1) {
      const past = byDate.get(shiftDate(today, -back))?.[metric];
      if (past != null) before.push(past);
    }
    const average = before.length >= 7 ? before.reduce((sum, v) => sum + v, 0) / before.length : null;
    // The strain of a day still running is not yet the day's: no verdict on it.
    const raw = average === null ? null : value - average;
    const delta = raw === null ? null : metric === "strain" ? Math.round(raw * 10) / 10 : Math.round(raw);
    trends.push({
      metric,
      label: LABEL[metric],
      value: show(metric, value),
      delta,
      deltaLabel: delta === null || delta === 0 ? null : showDelta(metric, delta),
      better: delta === null || delta === 0 || metric === "strain" ? null : delta > 0,
      ...(metric === "recoveryPct" ? { tone: recoveryTone(value) } : {}),
      spark: Array.from(
        { length: SPARK_DAYS },
        (_, index) => byDate.get(shiftDate(today, index - SPARK_DAYS + 1))?.[metric] ?? null,
      ),
    });
  }
  return trends;
}

/**
 * An SVG path through the values, broken where one is missing, scaled to
 * the box with a pixel to spare so the line is never cut by its own edge.
 */
export function sparkPath(values: (number | null)[], width: number, height: number): string {
  const present = values.filter((value): value is number => value !== null);
  if (present.length < 2) return "";
  const low = Math.min(...present);
  const high = Math.max(...present);
  const span = high - low || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  let path = "";
  let drawing = false;
  values.forEach((value, index) => {
    if (value === null) {
      drawing = false;
      return;
    }
    const x = Math.round(index * step * 10) / 10;
    const y = Math.round((1 + (height - 2) * (1 - (value - low) / span)) * 10) / 10;
    path += `${drawing ? "L" : "M"}${x} ${y} `;
    drawing = true;
  });
  return path.trim();
}
